"use client";

import { useEffect, useState } from "react";
import Modal from "react-modal";
import { X, User } from "lucide-react";
import { useGetVehicletype, useUpdateUserStatus } from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";
import { toast } from "react-toastify";

interface UserStatusModalProps {
  isOpen: boolean;
  userId: number;
  onClose: () => void;
  oldStatus?: number;
  onStatusUpdated?: (nextStatus: number) => void;
}

const UserStatusModal = ({
  isOpen,
  userId,
  onClose,
  oldStatus,
  onStatusUpdated
}: UserStatusModalProps) => {
  const { mutate: updateStatusMutation, data: driverData } =
    useUpdateUserStatus();
  const [toggleCarrier, setToggleCarrier] = useState("0");
  useEffect(() => {
    if (isOpen) {
      if (oldStatus !== null && oldStatus !== undefined) {
        setToggleCarrier(oldStatus.toString());
      } else {
        setToggleCarrier("1");
      }
    }
  }, [isOpen, oldStatus]);

 
  const [currectStatus, setCurrentStatus] = useState<string>("0");
  useEffect(() => {
    if (typeof window !== "undefined") {
      const nextApp = document.getElementById("__next");
      if (nextApp) Modal.setAppElement(nextApp);
    }
  }, []);
  const onSaveClick = () => {
    if (toggleCarrier == oldStatus?.toString() || toggleCarrier === "0") {
      return;
    }
    updateStatusMutation(
      {
        isActive: toggleCarrier,
        id: userId,
      },
      {
        onSuccess: (data: any) => {
          if (data?.status === "success") {
            toast.success("User status updated successfully");
            onStatusUpdated?.(Number(toggleCarrier)); 
            onClose();
          } else {
            toast.error("Failed to update user status");
          }
        },
        onError: (error: any) => {
          toast.error("Something went wrong while updating status");
        },
      }
    );
  };

  const handleCarrierToggle = (buttonId: string) => {
    setToggleCarrier(buttonId);
    setCurrentStatus(buttonId);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      overlayClassName="fixed inset-0 bg-transparent bg-opacity-40 z-50 flex items-center justify-center"
      className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-hidden focus:outline-none"
      contentLabel="Claim Booking Modal"
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between border-b border-gray-300 pb-2">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <User
              size={18}
              className="w-6 h-6 text-green-600 border-2 border-2-green-600 rounded-full"
            />{" "}
            User Status
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-red-500 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto space-y-6 mt-8 pr-2">
          <div className="grid grid-cols-3 gap-6 py-4">
            {[
              { id: "1", label: "Active" },
              { id: "2", label: "On Hold" },
              { id: "3", label: "Black Listed" },
              { id: "4", label: "Inactive" },
              { id: "5", label: "Unapproved" },
              { id: "6", label: "Deleted" },
            ].map((option) => {
              const isActive = toggleCarrier === option.id;

              return (
                <div
                  key={option.id}
                  className="flex items-center gap-2 text-sm sm:gap-3"
                >
                  <div
                    onClick={() => handleCarrierToggle(option.id)}
                    className={`cursor-pointer rounded-full border-2 
              ${
                isActive
                  ? "border-green-600 bg-green-100"
                  : "border-red-600 bg-red-100"
              } 
              flex items-center px-1 transition-all duration-300
              ${isActive ? "justify-end" : "justify-start"}
              min-w-[60px] min-h-[28px]`}
                  >
                    <div
                      className={`h-4 w-4 sm:h-5 sm:w-5 rounded-full shadow-md transition-all duration-300 
                ${isActive ? "bg-green-600" : "bg-red-600"}`}
                    />
                  </div>
                  <p className="text-[13px] font-medium text-gray-700">
                    {option.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-300 mt-4">
          <button
            onClick={() => onSaveClick()}
            className="bg-blue-600 hover:bg-blue-700 text-white cursor-pointer px-4 py-2 rounded-lg text-sm transition"
          >
            Save
          </button>
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 cursor-pointer rounded-lg text-sm transition"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default UserStatusModal;
